import { useEffect, useState } from 'react';
import type { DragonTigerLegal, DragonTigerSide, DragonTigerView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

export function DragonTigerTable() {
  const t = useT();
  const view = useGame((s) => s.view) as DragonTigerView;
  const legal = useGame((s) => s.legal) as DragonTigerLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const [side, setSide] = useState<DragonTigerSide>('DRAGON');
  const [amount, setAmount] = useState<number>(view.config.minBet);
  const myResult = view.lastResult?.seats.find((r) => r.seat === mySeat);
  const dealing = view.phase === 'DEALING';
  const ended = view.phase === 'HAND_END';
  const sideName = (s: DragonTigerSide) => (s === 'DRAGON' ? t('dt.dragon') : s === 'TIGER' ? t('dt.tiger') : t('dt.tie'));

  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);

  const quick = legal?.kind === 'bet' ? [...new Set([1, 2, 5, 10, 25].map((k) => legal.min * k).filter((v) => v <= legal.max).concat(legal.max))].sort((a, b) => a - b) : [];
  const betsOn = (s: DragonTigerSide) => view.players.filter((p) => p && p.status === 'BET' && p.side === s) as NonNullable<DragonTigerView['players'][number]>[];
  const pays = (s: DragonTigerSide) => (s === 'TIE' ? view.config.tiePays : 1);

  const hand = (s: 'DRAGON' | 'TIGER', card: string | null) => {
    const won = view.winner === s;
    return (
      <div key={s} className="card glass col" style={{ flex: 1, padding: 8, gap: 6, minWidth: 0, alignItems: 'center', borderColor: won ? 'var(--accent)' : undefined, boxShadow: won ? '0 0 0 2px var(--accent)' : undefined }} aria-label={sideName(s)}>
        <strong>{sideName(s)}</strong>
        <div style={{ display: 'flex', minHeight: narrow ? 52 : 68, alignItems: 'center' }}>{card ? <PlayingCard id={card} size="sm" /> : <span className="dim">–</span>}</div>
        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('bc.pays', { n: pays(s) })}</span>
      </div>
    );
  };

  const betList = (s: DragonTigerSide) => {
    const list = betsOn(s);
    return (
      <div key={s} className="row" style={{ gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', minWidth: 52 }}>{sideName(s)}</span>
        {list.length === 0 ? (
          <span className="dim" style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.45)' }}>–</span>
        ) : (
          list.map((p) => (
            <span key={p.seat} className={`pill ${ended && p.payout > p.bet ? 'accent' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>
              {p.seat === mySeat ? t('cg.you') : name(p.seat)} · {p.bet}
              {ended && p.payout > 0 ? ` → ${p.payout}` : ''}
            </span>
          ))
        )}
      </div>
    );
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer">
          <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
            {hand('DRAGON', view.dragon)}
            {hand('TIGER', view.tiger)}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 8, minHeight: 28, flexWrap: 'wrap' }}>
            {ended && view.winner && (
              <span className="pill accent" role="status">
                {view.winner === 'TIE' ? t('dt.tieWins') : t('ab.wins', { side: sideName(view.winner) })}
              </span>
            )}
            {ended && myResult && (
              <span className={`pill ${myResult.delta > 0 ? 'accent' : myResult.delta < 0 ? 'danger' : ''}`} role="status">
                {myResult.delta > 0 ? t('ab.youWon', { n: myResult.delta }) : myResult.delta < 0 ? t('ab.youLost', { n: -myResult.delta }) : t('bc.push')}
              </span>
            )}
            {dealing && <span className="center-note">{t('ab.dealing')}</span>}
            {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
            {view.phase === 'BETTING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
          </div>
          <div className="card glass col" style={{ padding: 8, gap: 4 }} aria-label={t('bc.bets')}>
            {(['DRAGON', 'TIGER', 'TIE'] as const).map(betList)}
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('bj.placeBet')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="row" style={{ gap: 6 }} role="radiogroup" aria-label={t('ab.side')}>
              {(['DRAGON', 'TIGER', 'TIE'] as const).map((s) => (
                <button key={s} role="radio" aria-checked={side === s} className={`btn grow ${side === s ? 'primary' : ''}`} onClick={() => setSide(s)}>
                  {sideName(s)}
                </button>
              ))}
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick.map((v) => (
                <button key={v} role="radio" aria-checked={amount === v} className={`bid-chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', side, amount })}>
                {t('ab.bet', { n: amount, side: sideName(side) })}
              </button>
            </div>
          </div>
        )}
        {!betting && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' && me.side ? t('ab.yourBet', { side: sideName(me.side), n: me.bet }) : ended ? t('bj.handOver') : dealing ? t('ab.dealing') : view.phase === 'BETTING' ? t('bj.betting') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
